import React, { useState, useRef, lazy, Suspense } from "react"
import NavButtons from "../components/schedule-templates/NavButtons"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import NurseListContainer from "../components/NurseAssignments/NurseListView/NurseListContainer"
import { getNursePatients } from "../features/schedule/derivedAssignments"
import useFetchAnalyze from "../hooks/useFetchAnalyze"
import useFetchSchedules from "../hooks/useFetchSchedules/useFetchSchedules"
import useFetchTemplates from "../hooks/useFetchTemplates"
import NotesSection from "../components/DailyHuddle/NotesSection"
import DailyHuddleSkeleton, {
	ChartSkeleton
} from "../components/DailyHuddle/DailyHuddleSkeleton"
import NotificationPopup from "../components/DailyCalendarFiles/CustomCalendar/NotificationPopup"

// ApexCharts is ~1MB and adds 200-400ms to first paint when loaded eagerly.
// Defer it so the page text + cards paint immediately on tab switch and the
// chart fades in once its bundle is downloaded.
const BarChart = lazy(() => import("../components/DailyCalendarFiles/barchart"))

const Schedule = ({ sidebarOpen }) => {
	useFetchSchedules("AnalyzeSchedule")
	const { analyzeData } = useFetchAnalyze()
	const [notification, setNotification] = useState({ visible: false, message: "", success: true })

	const { templatesData } = useFetchTemplates()

	const patientSchedule = useSelector(state => state.schedule.patientSchedule)
	const nurseSchedule = useSelector(state => state.schedule.nurseSchedule)
	const chargeNurse = useSelector(state => state.schedule.chargeNurse)
	// const preDay = useSelector((state) => state.analyze.preDayAnalysis);

	const [showChart, setShowChart] = useState(0) // State to control which chart is shown
	const [chartKey, setChartKey] = useState(0) // Unique key for forcing re-render

	// eslint-disable-next-line no-unused-vars
	const [isCollapsed, setIsCollapsed] = useState(true)

	const navigate = useNavigate()

	const nurseListRef = useRef(null) // Reference to the #nurse-list section

	const handleViewMoreClick = index => {
		// Check which card was clicked and update the state accordingly
		if (index === 0) {
			setShowChart(index) // Show the first chart
			setChartKey(prevKey => prevKey + 1) // Update the key to force chart re-render
			scrollToNurseList() // Scroll to nurse-list section
		} else if (index === 1) {
			navigate("/ic-tracker") // Navigate to another route
		} else if (index === 2) {
			setShowChart(index) // Show the third chart (with acuity value)
			setChartKey(prevKey => prevKey + 1) // Update the key to force chart re-render
			scrollToNurseList() // Scroll to nurse-list section
		}
	}

	// function generateDetailedBulletPoints() {
	//   const { chairPreferenceMismatches, isolationStatusMismatches, ineligibleNursesCount } = preDay;

	//   const acuityTable = settings.acuityTable;

	//   // Generate chair mismatch details
	//   const chairMismatchDetails = chairPreferenceMismatches.length > 0
	//     ? chairPreferenceMismatches.map((patientId) => {
	//         const patient = patientSchedule.find((p) => p.patientId === patientId);
	//         if (!patient) return `Patient ID ${patientId} not found.`;

	//         const treatment = acuityTable.find((t) => t.name === patient.infusionType);
	//         const chairPreferences = treatment?.rules?.find((rule) => rule.type === "resource")?.value || {};
	//         const preferredChairs = Object.entries(chairPreferences)
	//           .filter(([key, value]) => value === true)
	//           .map(([key]) => key);

	//         return `${patient.patientName}, receiving ${patient.infusionType}, is assigned to a mismatched chair.`
	//       })
	//     : [0];

	//   // Generate isolation mismatch details
	//   const isolationMismatchDetails = isolationStatusMismatches.length > 0
	//     ? isolationStatusMismatches.map((patientId) => {
	//         const patient = patientSchedule.find((p) => p.patientId === patientId);
	//         if (!patient) return `Patient ID ${patientId} not found.`;

	//         const treatment = acuityTable.find((t) => t.name === patient.infusionType);
	//         const isolationPreferences = treatment?.rules?.find((rule) => rule.type === "isolation")?.value || {};
	//         const preferredIsolation = Object.entries(isolationPreferences)
	//           .filter(([key, value]) => value === true)
	//           .map(([key]) => key);
	//         return `${patient.patientName}, receiving ${patient.infusionType}, prefers ${preferredIsolation.join(", ") || "None specified"} isolation but is not assigned to it`;
	//       })
	//     : [0];

	//   // Generate ineligible nurse details
	//   const ineligibleNursesDetails = patientSchedule.some((patient) => {
	//     const treatment = acuityTable.find((t) => t.name === patient.infusionType);
	//     const eligibleNurseIds = treatment?.eligibleNurses.map((nurse) => nurse.nurseId) || [];
	//     return !eligibleNurseIds.includes(patient.assignedNurse);
	//   })
	//     ? patientSchedule
	//         .filter((patient) => {
	//           const treatment = acuityTable.find((t) => t.name === patient.infusionType);
	//           const eligibleNurseIds = treatment?.eligibleNurses.map((nurse) => nurse.nurseId) || [];
	//           return !eligibleNurseIds.includes(patient.assignedNurse);
	//         })
	//         .map((patient) => {
	//           const treatment = acuityTable.find((t) => t.name === patient.infusionType);
	//           const assignedNurseName = nurseSchedule.find((nurse) => nurse.nurseId === patient.assignedNurse)?.nurseName || 'Unknown Nurse';
	//           return `${patient.patientName}, receiving ${patient.infusionType}, is assigned to an ineligible nurse.`
	//         })
	//     : [0];

	//   return [
	//     ...chairMismatchDetails,
	//     ...isolationMismatchDetails,
	//     ...ineligibleNursesDetails,
	//   ];
	// }

	// Function to scroll to the nurse-list section
	const scrollToNurseList = () => {
		if (nurseListRef.current) {
			nurseListRef.current.scrollIntoView({ behavior: "smooth" })
		}
	}

	if (!analyzeData) {
		return <DailyHuddleSkeleton sidebarOpen={sidebarOpen} />
	}

	const cardData = [
		{
			image: "/images/patients.svg",
			number: `${
				analyzeData.totalScheduledPatients +
				Math.round(analyzeData.totalAddOns.total) -
				Math.round(analyzeData.totalNoShows.total)
			}`, // You can adjust this dynamically
			caption: "Expected Patients",
			viewMoreLink: "#nurse-list"
		},
		{
			image: "/images/nursing_acuity.svg",
			number: `${analyzeData.acuityRange.min}-${analyzeData.acuityRange.max}`, // Example dynamic data
			caption: "Nursing Acuity Range",
			viewMoreLink: "/ic-tracker"
		},
		{
			image: "/images/high_acuity.svg",
			number: `${analyzeData.highAcuityPatients}`, // Example high acuity patient count
			caption: "High Acuity Patients",
			viewMoreLink: "/high-acuity-patients"
		}
	]

	function convertToCleanTime(timeStr) {
		// Split the time string into hours and minutes
		let [hours, minutes] = timeStr.split(":").map(Number)

		// Determine whether it's AM or PM
		const period = hours >= 12 ? "PM" : "AM"

		// Convert hours to 12-hour format
		hours = hours % 12 || 12 // If hours is 0 or 12, set to 12 (for 12-hour format)

		// If minutes are 00, remove the minutes part for a cleaner format
		const cleanTime = minutes === 0 ? `${hours} ${period}` : `${hours}:${minutes} ${period}`

		return cleanTime
	}

	function findPeakAddOns(utilization) {
		let maxAddOnsSum = 0
		let startTime = 0
		let endTime = 0

		for (let i = 0; i < utilization.length - 1; i++) {
			// Sum addOns for two consecutive time slots (for a 1-hour window)
			const currentAddOnsSum = utilization[i].addOns + utilization[i + 1].addOns

			// Check if the current sum is greater than the max sum found so far
			if (currentAddOnsSum > maxAddOnsSum) {
				maxAddOnsSum = currentAddOnsSum
				startTime = utilization[i].time
				endTime = utilization[i + 1].time
			}
		}

		// Convert minutes to 'HH:MM' format
		function convertToTime(minutes) {
			const hours = Math.floor(minutes / 60)
				.toString()
				.padStart(2, "0")
			const mins = (minutes % 60).toString().padStart(2, "0")
			return `${hours}:${mins}`
		}

		return `${convertToCleanTime(convertToTime(startTime))} to ${convertToCleanTime(convertToTime(endTime))}`
	}

	const overtimeAlert = () => {
		// Filter nurses with overtime probability greater than 50%
		const nursesWithOvertime = nurseSchedule.filter(nurse => {
			const overtimeProb = analyzeData.overtimeProbability[nurse.nurseId]
			return overtimeProb > 0.5
		})

		// If no nurses have overtime probability > 50%, return a fallback message
		if (nursesWithOvertime.length === 0) {
			return <div>No / Minimal overtime is expected today.</div>
		}

		// Otherwise, return a list of nurse names with high overtime probability
		return (
			<div>
				<h3 className="mb-2">
					The following {nursesWithOvertime.length} nurse
					{nursesWithOvertime.length !== 1 ? "s" : ""} have a heavy patient assignment
					toward the end of their shift and are projected to have overtime:
				</h3>
				<ul className="list-disc list-inside">
					{nursesWithOvertime.map(nurse => (
						<li key={nurse.nurseId}>
							<strong>{nurse.nurseName}</strong>
						</li>
					))}
				</ul>
			</div>
		)
	}

	const chargeNurseFirstName = chargeNurse?.nurseName.split(" ")[0] // Get the first name of the nurse

	const nursesWithAcuity = nurseSchedule.map(nurse => ({
		...nurse,
		acuity: getNursePatients(patientSchedule, nurse.nurseId).reduce(
			(total, p) => total + p.acuity,
			0
		)
	}))

	const highThreshold = 13
	const mediumHighThreshold = 10
	const mediumThreshold = 8

	const highAcuityNurses = nursesWithAcuity.filter(n => n.acuity >= highThreshold)
	const mediumHighAcuityNurses = nursesWithAcuity.filter(
		n => n.acuity >= mediumHighThreshold && n.acuity < highThreshold
	)
	const mediumAcuityNurses = nursesWithAcuity.filter(
		n => n.acuity >= mediumThreshold && n.acuity < mediumHighThreshold
	)
	const lowAcuityNurses = nursesWithAcuity.filter(n => n.acuity < mediumThreshold)

	return (
		<div className={`${sidebarOpen ? "pl-64" : "pl-8"} ${isCollapsed ? "w-screen" : "w-8/12"}`}>
			<div className="">
				{notification.visible && (
					<NotificationPopup
						message={notification.message}
						success={notification.success}
						onUndo={() =>
							setNotification({ visible: false, message: "", success: true })
						}
					/>
				)}
				<div className="my-5 flex h-auto sm:h-16 flex-col sm:flex-row items-start sm:items-center justify-between bg-white px-4 sm:px-6 lg:px-8 print-hidden">
					<h1 className="font-bold text-4xl font-manrope text-custom-blue mb-4 sm:mb-0">
						Daily Huddle
					</h1>
				</div>

				<div className="bg-white px-4 sm:px-6 lg:px-8 print-hidden">
					<div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6">
						<NavButtons hideToggle={true} />
					</div>
				</div>

				<main className="py-10 print:block">
					<div className="px-4 sm:px-6 lg:px-8">
						<div className="text-center print:text-left">
							<div className="flex h-screen">
								{/* Main Content Section */}
								<div className={`w-full h-screen`}>
									{/* Daily Huddle Section */}
									<div className="">
										<p className="text-left text-md">
											Hi {chargeNurseFirstName},
										</p>
										<p className="mt-4 text-left text-md">
											Today, the team will see ~
											{analyzeData.totalScheduledPatients +
												Math.round(analyzeData.totalAddOns.total) -
												Math.round(analyzeData.totalNoShows.total)}{" "}
											patients from{" "}
											<strong>
												{convertToCleanTime(analyzeData.openTime)} to{" "}
												{convertToCleanTime(analyzeData.closeTime)}
											</strong>{" "}
											({Math.round(analyzeData.totalScheduledPatients)}{" "}
											scheduled, ~{Math.round(analyzeData.totalAddOns.total)}{" "}
											add-ons, ~{Math.round(analyzeData.totalNoShows.total)}{" "}
											no-shows and cancellations expected).
										</p>
										<p className="mt-4 text-left">
											The team has {analyzeData.highAcuityPatients} patients
											with high acuities (4 or 5) scheduled. All nurses have
											an acuity assignment between{" "}
											{analyzeData.acuityRange.min}-
											{analyzeData.acuityRange.max}.
										</p>
										<p className="mt-4 text-left">
											Most of your add-ons are expected around{" "}
											<strong>
												{findPeakAddOns(analyzeData.utilization)}
											</strong>
											.
										</p>
										<div className="mt-4 text-left">{overtimeAlert()}</div>
										<hr className="my-4 border-t border-gray-300" />
										<ul className="mt-4 text-left list-disc list-inside">
											<li>
												Very High Acuity Nurses: {highAcuityNurses.length}{" "}
												(Acuity Value: {">"} {highThreshold})
											</li>
											<li>
												High Acuity Nurses: {mediumHighAcuityNurses.length}{" "}
												(Acuity Value: {mediumHighThreshold} -{" "}
												{highThreshold})
											</li>
											<li>
												Medium Acuity Nurses: {mediumAcuityNurses.length}{" "}
												(Acuity Value: {mediumThreshold} -{" "}
												{mediumHighThreshold})
											</li>
											<li>
												Low Acuity Nurses: {lowAcuityNurses.length} (Acuity
												Value: {"<"}
												{mediumThreshold})
											</li>
										</ul>
										{/* Add Bullet Point Section */}
										{/*}
                    {generateDetailedBulletPoints().every((point) => point === 0) ? (
                      <p className="mt-4 text-left">All patient assignments align with their preferences.</p>
                    ) : (
                      <div>
                        <p className="font-bold text-left">The following patients have assignments that do not align with their preferences:</p>
                        <ul className="mt-4 text-left list-disc list-inside">
                          {generateDetailedBulletPoints()
                            .filter((point) => point !== 0)
                            .map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                        </ul>
                      </div>
                    )}*/}
									</div>

									{/* Warning Section */}
									{/* <div className="p-4 flex flex-col gap-4 items-center">
										{analyzeData &&
											analyzeData.warnings &&
											analyzeData.warnings.map((warning, index) => (
												<Warning key={index} warning={warning} />
											))}
									</div> */}

									{/* Notes Section */}
									<NotesSection />

									{/* Card Section */}
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										{cardData.map((item, index) => (
											<div
												key={index}
												className="bg-white shadow-md rounded-lg p-1 flex flex-col justify-between"
											>
												{/* Icon + Text Section */}
												<div className="flex items-start p-4">
													<img
														src={item.image}
														alt="Icon"
														className="h-10 w-10 mr-4"
													/>
													{/* Text Section */}
													<div className="text-left">
														<h4 className="text-gray-500 text-md font-medium">
															{item.caption}
														</h4>
														<div className="text-4xl font-bold text-gray-900">
															{item.number}
														</div>
													</div>
												</div>

												{/* View More Section - centered at the bottom */}
												<div className="py-3 flex justify-center bg-customBg">
													<p
														onClick={() => handleViewMoreClick(index)}
														className="text-customBlue hover:text-customBlue-dark hover:cursor-pointer text-sm font-semibold"
													>
														View More
													</p>
												</div>
											</div>
										))}
									</div>

									{/* Bar Chart Section */}
									<Suspense fallback={<ChartSkeleton />}>
										<div className="my-6 p-6 bg-white shadow-md rounded-lg">
											<h3 className="text-2xl font-bold mb-4">
												Today's Forecast
											</h3>
											<BarChart
												analyze={analyzeData}
												templatesData={templatesData}
												showCancellations={true}
												showAddOns={true}
											/>
										</div>
									</Suspense>

									<div
										id="nurse-list"
										ref={nurseListRef}
										className="overflow-x-scroll"
									>
										{showChart === 0 && (
											<NurseListContainer
												key={chartKey}
												setNotification={setNotification}
												isToday={true}
											/>
										)}
										{showChart === 2 && (
											<NurseListContainer
												acuityValue={[4, 5]}
												key={chartKey}
												setNotification={setNotification}
												isToday={true}
											/>
										)}
									</div>
								</div>

								{/* <SuggestionSidebar
									isCollapsed={isCollapsed}
									setIsCollapsed={setIsCollapsed}
								/> */}
							</div>
						</div>
					</div>
				</main>
			</div>
		</div>
	)
}

export default Schedule
